/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import trI18n from '@/common/utils/trI18n';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { setPanelSelector, closePanelTabSelector } from '@/state/selector/panelSelector';
import './AiAutoLog.css';

import Constant from "@/common/constant/Constant";
import AiAutoConstant from "@/common/constant/AiAutoConstant";
import Utils from '@/common/utils/utils';

import {showLoadingMouse, hideLoadingMouse} from '@/common/utils/CustomCursor';
import { getAiAutoLog, retryQueryListAiAutoLog } from "../../ticketKnowledge/handler/DetailTicketHandler";
import { updateAiAutoLog } from '@/service/aiAutoLogService';
import { getTicketById } from '@/service/ticketService';

import CircularProgress from '@material-ui/core/CircularProgress';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { Button } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { PropTypes } from 'prop-types';
import { isLimitedTabsLength } from '@/service/userService';
import { multiPanelAtom } from '@/state/atom/panelAtom';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

AiAutoLog.propTypes = {
    corporationDetail: PropTypes.any,
    selectedProject: PropTypes.any,
    currentUserDetail: PropTypes.any,
    ticketDetail: PropTypes.any,
    panelId: PropTypes.any,
    isDisabled: PropTypes.any,
};

function AiAutoLog({
    corporationDetail,
    selectedProject,
    currentUserDetail,
    ticketDetail,
    panelId,
    isDisabled
}) {
    // Re-render when the translation dictionary loads/updates
    useTranslationSubscription();

    const [isExpand, setIsExpand] = useState(false)
    const [listAiAutoLog, setListAiAutoLog] = useState([]);
    const [isShowSeeMore, setIsShowSeeMore] = useState(false);
    const [isAutoDeleteLogSetting, setIsAutoDeleteLogSetting] = useState(false);
    const [isLoadingGetLog, setIsLoadingGetLog] = useState(false);
    const [errorMessagePopup, setErrorMessagePopup] = useState({ isShow: false});
    const [pausingLogList, setPausingLogList] = useState([]);
    const retryQueryLogsInterval = useRef(null);
    const toastRef = useRef(null);
    const panelList = useRecoilValue(multiPanelAtom);
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const addNewPanel = useSetRecoilState(setPanelSelector);
    const closePanelTab = useSetRecoilState(closePanelTabSelector);

    useEffect(() => {
        let promptData = Utils.convertJSONDataToObject(ticketDetail.promptData);
        let autoDeleteLog = true;
        if (!Utils.isEmptyObject(promptData)) {
            if (typeof promptData.autoDeleteLog === "boolean") {
                autoDeleteLog = promptData.autoDeleteLog;
            }
        }
        setIsAutoDeleteLogSetting(autoDeleteLog);
    }, []);

    // handle tracking pausing log list to update log status
    useEffect(() => {
        if (Utils.isEmptyArray(pausingLogList)) return;

        if (retryQueryLogsInterval.current) {
            clearInterval(retryQueryLogsInterval.current);
        }
        retryQueryLogsInterval.current = setInterval(async() => {
            const args = {
                corporationDetail,
                selectedProject,
                currentUserDetail,
            }
            const listLogId = pausingLogList.map(log => log.id);
            try {
                const {listLog: retriedLogList} = await retryQueryListAiAutoLog(args, ticketDetail.id, listLogId);
                
                if (!retriedLogList || Utils.isEmptyArray(retriedLogList)) {
                    setPausingLogList([]);
                    clearInterval(retryQueryLogsInterval.current);
                    return;
                }

                const retriedLogMap = new Map(retriedLogList.map(log => [log.id, log]));
                // handle update pausing log
                setListAiAutoLog(prev =>
                    prev.map(log => {
                        const retryLog = retriedLogMap.get(log.id);
                        if (!retryLog) return log;

                        return {
                            ...log,
                            status: retryLog.status,
                            updatedAt: retryLog.updatedAt,
                            ...(retryLog.endTime && { endTime: retryLog.endTime })
                        };
                    })
                );

                setPausingLogList(prev =>
                    prev.filter(pausing => {
                        const retry = retriedLogMap.get(pausing.id);
                        return retry?.status === AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING;
                    })
                );
                
                retriedLogList.forEach((log, index) => {
                    const logStatus = log.status;
                    switch (logStatus) {
                        case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSED:
                            enqueueSnackbar(buildSnackbarMessage('MSG-AI-LOG-PAUSED-SUCCESSFULLY'), {
                                variant: 'success',
                                autoHideDuration: 3000,
                                key: `${Date.now()}-${Math.random()}`
                            });
                            break;
                        case AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING:
                        case AiAutoConstant.AICHATTHREAD_LOG_STATUS.DONE:
                        case AiAutoConstant.AICHATTHREAD_LOG_STATUS.ERROR:
                            enqueueSnackbar(buildSnackbarMessage('MSG-AI-LOG-PAUSED-FAILED'), {
                                variant: 'error',
                                autoHideDuration: 3000,
                                key: `${Date.now()}-${Math.random()}`
                            });
                            break;
                        default:
                            break;
                    }
                });

            } catch (error) {
                console.log('retryQueryListAiAutoLog error: ', error);
                setPausingLogList([]);
                clearInterval(retryQueryLogsInterval.current);
            }

        }, 30000); // 30s

        return () => clearInterval(retryQueryLogsInterval.current);
    }, [pausingLogList]);
    
    const handleReloadLogList = async () => {
        setIsLoadingGetLog(true);
        if (retryQueryLogsInterval) {
            clearInterval(retryQueryLogsInterval.current);
        }

        const args = {
            corporationDetail,
            selectedProject,
            currentUserDetail,
            limit: listAiAutoLog.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT ? AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT : listAiAutoLog.length
        }
        const {listLog: reloadList, error} = await getAiAutoLog(args, ticketDetail.id);
        if (!Utils.isEmptyString(error)) {
            setErrorMessagePopup({isShow: true, msgCode: error})
            return;
        }

        const pausingLogSet = new Set(pausingLogList.map(p => p.id));
        const reLoadPausingList = reloadList.filter(log => pausingLogSet.has(log.id));
        if (reLoadPausingList.length > 0) {
            setPausingLogList(prev => {
                const reLoadPausingSet = new Map(reLoadPausingList.map(p => [p.id, p.status]));
                return prev.filter(clone => {
                    const status = reLoadPausingSet.get(clone.id);
                    return status === AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING;
                });
            });

            reLoadPausingList.forEach((log, index) => {
                const logStatus = log.status;
                switch (logStatus) {
                    case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSED:
                        enqueueSnackbar(buildSnackbarMessage('MSG-AI-LOG-PAUSED-SUCCESSFULLY'), {
                            variant: 'success',
                            autoHideDuration: 3000,
                            key: `${Date.now()}-${Math.random()}`
                        });
                        break;
                    case AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING:
                    case AiAutoConstant.AICHATTHREAD_LOG_STATUS.DONE:
                    case AiAutoConstant.AICHATTHREAD_LOG_STATUS.ERROR:
                        enqueueSnackbar(buildSnackbarMessage('MSG-AI-LOG-PAUSED-FAILED'), {
                            variant: 'error',
                            autoHideDuration: 3000,
                            key: `${Date.now()}-${Math.random()}`
                        });
                        break;
                    default:
                        break;
                }
            });
        }

        setListAiAutoLog(reloadList);
        setIsShowSeeMore((!reloadList.length || reloadList.length < listAiAutoLog.length || reloadList.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT) ? false : true);

        setIsLoadingGetLog(false);
    }
    
    const handleSeeMore = async () => {
        try {
            showLoadingMouse();

            const currentLastLog = listAiAutoLog[listAiAutoLog.length - 1];

            const args = {
                corporationDetail,
                selectedProject,
                currentUserDetail,
                lastEvaluatedKey: {
                    id: { S: currentLastLog.id },
                    createdAt: { S: currentLastLog.createdAt },
                    ticketId: { S: currentLastLog.ticketId }
                },
                limit: AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT
            }
            const {listLog: getMoreList, error} = await getAiAutoLog(args, ticketDetail.id);
            if (!Utils.isEmptyString(error)) {
                setErrorMessagePopup({isShow: true, msgCode: error})
                return;
            }
            setListAiAutoLog(prev => [...prev, ...getMoreList]);
            setIsShowSeeMore((!getMoreList.length || getMoreList.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT) ? false : true);

            hideLoadingMouse();
        } catch (error) {
            console.log('handleSeeMore error :', error );
            hideLoadingMouse();
        }
    }

    const handleOpenTabLog = async (logDetail) => {
        if (!logDetail) return;
        const existTicket = await getTicketById(null, ticketDetail.id);
        if (!existTicket) {
            setErrorMessagePopup({isShow: true, msgCode: Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR})
            return;
        }

        const panelDataChatAI = panelList?.find(
            panel => panel?.type === Constant.PANEL_TYPE.OPEN_CHAT_AI
        );
        const isLimited = await isLimitedTabsLength({
            panelDataChatAI,
            projectInuseDetail,
            userDetail: currentUserDetail
        });

        if (isLimited) {
            enqueueSnackbar(buildSnackbarMessage('MSG-CM-WARNING-LIMIT-TAB'), {
                variant: 'error',
                autoHideDuration: 3000,
                style: { whiteSpace: 'pre-line' }
            });
            const tabList = panelDataChatAI?.tabList || [];
            if (!Utils.isEmptyArray(tabList) && !panelDataChatAI?.isDisplay) {
                const lastTab = tabList[tabList.length - 1];
                addNewPanel({
                    type: Constant.PANEL_TYPE.OPEN_CHAT_AI,
                    tabId: lastTab.id,
                });
            }
            return;
        }

        // delay Loading to PanelTab re-render (performance)
        showLoadingMouse();
        setTimeout(() => {
            addNewPanel({
                type: Constant.PANEL_TYPE.OPEN_CHAT_AI,
                tabId: logDetail.chatThreadId,
                currentPanelId: panelId,
                filterKnowledgeListData: {
                    resetSearchCondition: false,
                    usingBasicPrompt: true,
                    isInvalidPrecondition: false,
                    threadId: logDetail.chatThreadId,
                    projectSelectedId: selectedProject.id,
                    aiAutoLog: logDetail
                },
            });
            hideLoadingMouse();
        }, 1000);

    }

    const handleUpdateLogByAction = async (actionType, logDetail) => {
        if (isDisabled) return;
        try {
            showLoadingMouse();

            let listAiAutoLogClone = [...listAiAutoLog];
            let requestDto = {
                ticketId: ticketDetail.id,
                aiChatThreadId: logDetail.id,
                actionType: actionType,
                userId: currentUserDetail.id,
                corporationInuse: corporationDetail.id,
                projectInuse: selectedProject.id,
            }
            
            let response = await updateAiAutoLog(requestDto);
            
            let handledLogStatus = response.status;
            let logDetailUpdated = Utils.convertJSONDataToObject(response.data);

            if (!logDetailUpdated || Utils.isEmptyObject(logDetailUpdated)) {
                hideLoadingMouse();
                setErrorMessagePopup({ isShow: true, msgCode: response.errorCode })
                return;
            }

            switch (actionType) {
                case AiAutoConstant.THREAD_LOG_UPDATE_API.REPLAY_LOG:
                    listAiAutoLogClone = listAiAutoLogClone.map(log => 
                        log.id === logDetailUpdated.id ? logDetailUpdated : log
                    );
                    enqueueSnackbar(
                        buildSnackbarMessage(handledLogStatus ? 'MSG-AI-LOG-REPLAY-SUCCESSFULLY' : 'MSG-AI-LOG-REPLAY-FAILED'), {
                            variant: handledLogStatus ? 'success' : 'error',
                            autoHideDuration: 3000,
                            key: `${Date.now()}-${Math.random()}`
                    });
                    break;
                case AiAutoConstant.THREAD_LOG_UPDATE_API.PAUSE_LOG:
                    listAiAutoLogClone = listAiAutoLogClone.map(log => {
                        return log.id === logDetailUpdated.id ? logDetailUpdated : log
                    });

                    if (logDetailUpdated.status === AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING) {
                        setPausingLogList(pre => [...pre, logDetailUpdated]);
                    }

                    if (!handledLogStatus) {
                        enqueueSnackbar(buildSnackbarMessage('MSG-AI-LOG-PAUSED-FAILED'), {
                            variant: 'error',
                            autoHideDuration: 3000,
                            key: `${Date.now()}-${Math.random()}`
                        });
                    }
                    break;
                case AiAutoConstant.THREAD_LOG_UPDATE_API.DELETE_LOG:
                    listAiAutoLogClone = listAiAutoLogClone.filter(log => log.id !== logDetail.id);
                    
                    // handle get more 1 log to replace deleted log if isShowSeeMore == true
                    if (isShowSeeMore) {
                        const currentLastLog = listAiAutoLogClone[listAiAutoLogClone.length - 1];
                        const args = {
                            corporationDetail,
                            selectedProject,
                            currentUserDetail,
                            lastEvaluatedKey: {
                                id: { S: currentLastLog.id },
                                createdAt: { S: currentLastLog.createdAt },
                                ticketId: { S: currentLastLog.ticketId }
                            },
                            limit: 1
                        }
                        const {listLog: getMoreList} = await getAiAutoLog(args, ticketDetail.id);
                        if (!Utils.isEmptyArray(getMoreList)) {
                            listAiAutoLogClone = [...listAiAutoLogClone, ...getMoreList]
                        }
                    }

                    // close tabChatAI
                    closePanelTab({
                        event: null,
                        panelType: Constant.PANEL_TYPE.OPEN_CHAT_AI,
                        panelId: panelId,
                        tabId: logDetail.chatThreadId
                    });
                    break;
                default:
                    break;
            }
    
            setListAiAutoLog(listAiAutoLogClone);
            if (Utils.isEmptyArray(listAiAutoLogClone) || listAiAutoLogClone.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT) {
                setIsShowSeeMore(false);
            }
    
            hideLoadingMouse();
        } catch (error) {
            console.log("handleUpdateLogByAction error: ", error);
            hideLoadingMouse();
        }
    }

    const handleToggleAiAutoDeleteLog = async () => {
        if (isDisabled) return;
        try {
            setIsAutoDeleteLogSetting(!isAutoDeleteLogSetting)
            let requestDto = {
                ticketId: ticketDetail.id,
                aiChatThreadId: 'fake',
                actionType: AiAutoConstant.THREAD_LOG_UPDATE_API.UPDATE_AUTO_DELETE_LOG_FLAG,
                userId: currentUserDetail.id,
                corporationInuse: corporationDetail.id,
                projectInuse: selectedProject.id,
            }

            let response = await updateAiAutoLog(requestDto);
            if (response.status === false) {
                hideLoadingMouse();
                setErrorMessagePopup({
                    isShow: true,
                    msgCode: response.errorCode
                })
                setIsAutoDeleteLogSetting(!isAutoDeleteLogSetting)
                return;
            }
        } catch (error) {
            console.log("handleToggleAiAutoDeleteLog error: ", error);
            hideLoadingMouse();
        }
    }

    const renderLogStatus = (status) => {
        if (!status) return <></>;
        switch (status) {
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING:
                return <></>;
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSED:
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING:
                return <img src='/assets/image/status-stop.svg' alt='status-stop' />
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.DONE:
                return <img src='/assets/image/status-success.svg' alt='status-success' />
            case Constant.CHATTHREAD_STATUS.ERROR:
                return <img src='/assets/image/status-error.svg' alt='status-error' />
            default:
                return <></>;
        }
    }

    const renderLogTimer = (log) => {
        if (!log) return '';
        const startTime = log.startTime ? Utils.getJSTDateTimeYYYY_MM_DDHHMM(log.startTime) : '';
        const endTime = log.endTime ? Utils.getJSTDateTimeYYYY_MM_DDHHMM(log.endTime) : '';
        const updatedTime = Utils.getJSTDateTimeYYYY_MM_DDHHMM(log.updatedAt);
        
        let endStatus = '';
        const logStatus = log.status
        switch (logStatus) {
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING:
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING:
                endStatus = `${tr('RUNNING')}...`;
                break;
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSED:
                endStatus = `${tr('STOP')} ${updatedTime}`;
                break;
            default:
                endStatus = `${tr('END')} ${endTime}`;
                break;
        }
      
        return `${tr('START')} ${startTime}  ${endStatus}`;
    }

    const renderLogActionIcon = (log) => {
        let className = 'ai-log-btn';
        if (isDisabled) className += ' disabled';
        switch (log.status) {
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING:
                return (
                    <button onClick={async () => await handleUpdateLogByAction(AiAutoConstant.THREAD_LOG_UPDATE_API.PAUSE_LOG, log)} className={`btn-reset-default ${className}`}>
                        <img src='/assets/image/icon-pause.svg' alt='icon-pause' />
                    </button>
                )
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING:
                return (
                    <span className={className}>
                        <img className="option-icon icon-loading" src={`/assets/image/icon-loading.svg`} alt="icon-loading"/>
                    </span>
                )
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSED:
                return (
                    <>
                        <button onClick={async () => await handleUpdateLogByAction(AiAutoConstant.THREAD_LOG_UPDATE_API.REPLAY_LOG, log)} className={`btn-reset-default ${className}`}>
                            <img src='/assets/image/icon-play.svg' alt='icon-play' />
                        </button>
                        <button onClick={async () => await handleUpdateLogByAction(AiAutoConstant.THREAD_LOG_UPDATE_API.DELETE_LOG, log)} className={`btn-reset-default ${className}`}>
                            <img src='/assets/image/icon-trash.svg' alt='icon-trash' />
                        </button>
                    </>
                );
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.DONE:
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.ERROR:
                return (
                    <button onClick={async () => await handleUpdateLogByAction(AiAutoConstant.THREAD_LOG_UPDATE_API.DELETE_LOG, log)} className={`btn-reset-default ${className}`}>
                        <img src='/assets/image/icon-trash.svg' alt='icon-trash' />
                    </button>
                );
            default:
                return <></>
        }
    }

    const handleExpandAiAutoLog = async () => {
        if (!isExpand && Utils.isEmptyArray(listAiAutoLog)) {
            setIsLoadingGetLog(true);
            showLoadingMouse();
            try {
                const args = {
                    corporationDetail,
                    selectedProject,
                    currentUserDetail,
                    limit: AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT
                }
                // get init log list
                const {listLog: initList, error} = await getAiAutoLog(args, ticketDetail.id);
                if (!Utils.isEmptyString(error)) {
                    setErrorMessagePopup({isShow: true, msgCode: error})
                    setIsLoadingGetLog(false);
                    hideLoadingMouse();
                    return;
                }
                setListAiAutoLog(initList);
                setIsShowSeeMore(initList.length >= AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT);
                // case: f5 windows / change tab ticket
                let initPausingList = initList.filter(init => init.status === AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING);
                if (!Utils.isEmptyArray(initPausingList)) {
                    setPausingLogList(initPausingList)
                }
            } catch (error) {
                console.error("Error fetching AI auto logs:", error);
            }
            setIsLoadingGetLog(false);
            hideLoadingMouse();
        }

        setIsExpand(prev => !prev);
    }

    return (
        <SnackbarProvider
            maxSnack={10}
            ref={toastRef}
            anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            action={key => (
                <Button className="notistack-button" onClick={() => closeSnackbar(key)}>
                    <CloseIcon />
                </Button>
            )}
        >
            <div
                className="accordionHead-SettingTitle"
                onClick={async() => await handleExpandAiAutoLog()}
            >
                <img src="/assets/image/execute-log.svg" alt="execute-log.svg" />
                <span className="text">{trI18n('EXECUTION_LOG')}</span>
                {isExpand ? (
                    <img src="/static/media/icon-minus-accordion.24e40269.svg" alt="minus" />
                ) : (
                    <img src="/static/media/icon-plus-accordion.d9a73563.svg" alt="plus" />
                )}
            </div>
            {isLoadingGetLog ? (<div className="ai-auto-log-inner ai-log-flex justify-center"><CircularProgress color="primary" size="1.4rem"/></div>) : isExpand ? (
                <div className="ai-auto-log-inner">
                    <div className="ai-auto-log-sub-header ai-log-flex justify-between">
                        <div className="ai-log-flex justify-between gap-4">
                            <span className="ai-log-text f-weight-700">{trI18n('EXECUTION_LOG')}</span>
                            <button onClick={async () => await handleReloadLogList()} className="btn-reset-default ai-log-btn"><img src="/assets/image/reload-icon.svg" alt="reload-icon" /></button>
                        </div>
                        <div className="ai-log-flex justify-between gap-4">
                            <span className="ai-log-text f-weight-500">{trI18n('AI_AUTO_DELETE_LOG_SUBSCRIPTION')}</span>
                            <div className="switch-check">
                                <input
                                    name="ai-auto-delete-log-toggle"
                                    id="ai-auto-delete-log-toggle"
                                    type="checkbox"
                                    checked={isAutoDeleteLogSetting}
                                    disabled={isDisabled}
                                    onChange={e => {handleToggleAiAutoDeleteLog()}}
                                />
                                <label htmlFor="ai-auto-delete-log-toggle"></label>
                            </div>
                        </div>
                    </div>
                    <div className="ai-log-flex vertical gap-8 ai-log-list ">
                        {listAiAutoLog.length > 0 ? listAiAutoLog.map(log => {
                            return (
                                <div className="log-ai-detail-wrapper ai-log-flex" key={log.id}>
                                    <div className="log-ai-status">
                                        {renderLogStatus(log?.status)}
                                    </div>
                                    <button className="btn-reset-default log-ai-runtime ai-log-text" onClick={async() => await handleOpenTabLog(log)}>
                                        {renderLogTimer(log)}
                                    </button>
                                    <div className="log-ai-action ai-log-flex justify-end gap-8">
                                        {renderLogActionIcon(log)}
                                    </div>
                                </div>
                            )
                        }) : (
                            <p style={{margin: '0 auto'}}>{trI18n('EMPTY_EXECUTION_LOG')}</p>
                        )}
                    </div>
                    <div className="ai-log-footer ai-log-flex justify-end">
                        {isShowSeeMore && <button onClick={async() => await handleSeeMore()} className="btn-reset-default ai-log-see-more">{trI18n('COMBTN009')}</button>}
                    </div>
                </div>
            ) : (<></>)}
            
            <ConfirmDialog
                isOpen={errorMessagePopup.isShow}
                msgConfirm={trI18n(errorMessagePopup?.msgCode || 'MSG-AI-LOG-CHANGED-DATA')}
                textSubmit={tr('OK')}
                okCallBack={() => {
                    setErrorMessagePopup({...errorMessagePopup, isShow: false})
                    window.location.reload();
                }}
            />
        </SnackbarProvider>
    );
}

export default AiAutoLog;